<script>
import SnackbarContainer from './components/SnackBar/Container.vue';

export default {
  components: { SnackbarContainer },
  data() {
    return { theme: 'light' };
  },
  mounted() {
    this.setColorTheme();
    this.listenToThemeChanges();
    this.setLocale(window.osdeskConfig.selectedLocale);
  },
  methods: {
    setColorTheme() {
      const isPortfolioRoute =
        window.location.pathname === '/' ||
        window.location.pathname.startsWith('/portfolio') ||
        ['/features', '/workflow', '/pricing', '/customers'].includes(
          window.location.pathname
        );
      if (isPortfolioRoute) {
        this.theme = 'light';
        document.documentElement.classList.remove('dark');
        return;
      }
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        this.theme = 'dark';
        document.documentElement.classList.add('dark');
      } else {
        this.theme = 'light';
        document.documentElement.classList.remove('dark');
      }
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');

      mql.onchange = e => {
        if (e.matches) {
          this.theme = 'dark';
          document.documentElement.classList.add('dark');
        } else {
          this.theme = 'light';
          document.documentElement.classList.remove('dark');
        }
      };
    },
    setLocale(locale) {
      if (locale) {
        this.$root.$i18n.locale = locale;
      }
    },
  },
};
</script>

<template>
  <div class="h-full min-h-screen w-full antialiased" :class="theme">
    <router-view />
    <SnackbarContainer />
  </div>
</template>

<style lang="scss">
@tailwind base;
@tailwind components;
@tailwind utilities;

@import '../dashboard/assets/scss/next-colors';

html,
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif;
  @apply h-full w-full;

  input,
  select {
    outline: none;
  }
}

.text-link {
  @apply text-n-brand font-medium hover:text-n-blue-10;
}

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}

/* Portfolio Custom Stitch Styles */
.material-symbols-outlined {
  font-variation-settings:
    'FILL' 0,
    'wght' 400,
    'GRAD' 0,
    'opsz' 24;
  vertical-align: middle;
  display: inline-block;
  line-height: 1;
}

.ai-gradient-button {
  background: linear-gradient(135deg, #001b44 0%, #006d40 100%);
}

.glass-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(226, 232, 240, 0.8) !important;
}

.glass-panel {
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(226, 232, 240, 0.5) !important;
}

.bento-grid {
  display: grid !important;
  grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
  gap: 24px !important;
}

@media (min-width: 768px) {
  .bento-grid {
    grid-template-columns: repeat(12, 1fr) !important;
  }
}

.ai-gradient-text,
.gradient-text {
  background: linear-gradient(135deg, #001b44 0%, #006d40 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
}

.ai-glow-soft {
  box-shadow: 0 0 40px rgba(88, 255, 168, 0.15) !important;
}

.ai-glow-border {
  box-shadow: 0 0 20px rgba(88, 255, 168, 0.2) !important;
  border-color: #30e18e !important;
  position: relative;
}

.ai-glow-border::after {
  content: '';
  position: absolute;
  inset: -2px;
  background: linear-gradient(45deg, #001b44, #58ffa8, #006d40);
  z-index: -1;
  border-radius: inherit;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.ai-glow-border:hover::after {
  opacity: 0.15;
}

.chat-bubble-ai {
  background-color: #f8f9ff !important;
  border-left: 4px solid #58ffa8 !important;
}

.ai-shimmer {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(88, 255, 168, 0.1),
    transparent
  );
  background-size: 200% 100%;
  animation: shimmer 4s infinite linear;
}

.shimmer {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 2.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.ai-pulse {
  animation: pulse-glow 3s infinite ease-in-out;
}

@keyframes pulse-glow {
  0%,
  100% {
    box-shadow: 0 0 20px rgba(88, 255, 168, 0.2);
    border-color: rgba(0, 109, 64, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(88, 255, 168, 0.5);
    border-color: rgba(88, 255, 168, 0.8);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.ai-border-glow:focus-within {
  border-color: #30e18e !important;
  box-shadow: 0 0 0 4px rgba(48, 225, 142, 0.15) !important;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c4c6d2;
  border-radius: 10px;
}

/* Policy & Documentation Sidebar Styles */
.section-active,
.sidebar-link-active {
  color: #001b44 !important;
  border-left: 2px solid #006d40 !important;
  background-color: #effff8 !important;
  font-weight: 600 !important;
}

.ai-glass-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(226, 232, 240, 0.8) !important;
  box-shadow: 0 10px 30px -10px rgba(0, 30, 66, 0.05) !important;
}

.ai-glow-bg {
  background: radial-gradient(
    circle at top right,
    #effff8 0%,
    transparent 70%
  ) !important;
}

.resolution-line {
  background: linear-gradient(90deg, #001e42 0%, #006d40 100%) !important;
}

.security-gradient {
  background: radial-gradient(
      circle at 0% 0%,
      rgba(0, 47, 108, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 100% 100%,
      rgba(0, 109, 64, 0.1) 0%,
      transparent 50%
    ) !important;
}

.ai-accent-gradient {
  background: linear-gradient(135deg, #001b44 0%, #006d40 100%) !important;
}

.icon-fill {
  font-variation-settings: 'FILL' 1 !important;
}
</style>
